import { useState } from 'react';
import { useFetch, useClasses, useSubmit } from '../hooks';
import { Table, Modal, PageHeader, Select, FormField, StatusBadge, InfoRow, Avatar, Badge } from '../components/UI';
import { Plus, Download, BookOpen, User, BarChart2 } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// ====== TEACHERS PAGE ======
export function Teachers() {
  const { data: teachers, loading, refetch } = useFetch('/teachers');
  const [showAdd, setShowAdd] = useState(false);
  const [showView, setShowView] = useState(null);
  const [form, setForm] = useState({ full_name:'', email:'', phone:'', password:'', qualification:'', specialization:'', designation:'', joining_date:'', salary:'', whatsapp:'' });
  const [photo, setPhoto] = useState(null);
  const { loading: saving, submit } = useSubmit();

  const setF = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleAdd = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
    if (photo) fd.append('photo', photo);
    const r = await submit(() => api.post('/teachers', fd), 'Teacher added!');
    if (r.success) { setShowAdd(false); refetch(); }
  };

  const columns = [
    { label: 'Teacher', render: r => <div style={{ display:'flex', alignItems:'center', gap:10 }}><Avatar src={r.photo_url} name={r.full_name}/><div><div style={{fontWeight:700}}>{r.full_name}</div><div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{r.employee_id}</div></div></div> },
    { label: 'Designation', key: 'designation' },
    { label: 'Specialization', key: 'specialization' },
    { label: 'Qualification', key: 'qualification' },
    { label: 'Phone', key: 'phone' },
    { label: '', render: r => <button className="btn btn-secondary btn-sm" onClick={() => setShowView(r)}>View</button> },
  ];

  return (
    <div>
      <PageHeader title="Teachers" subtitle={`${teachers?.length || 0} staff members`}
        actions={<button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={15}/> Add Teacher</button>} />
      <div className="card"><Table columns={columns} data={teachers} loading={loading} /></div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Teacher" size="lg">
        <form onSubmit={handleAdd}>
          <div style={{ textAlign:'center', marginBottom:20 }}>
            <label style={{ cursor:'pointer' }}>
              <div style={{ width:80, height:80, borderRadius:'50%', border:'3px dashed var(--border-strong)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--surface-alt)', margin:'0 auto' }}>
                {photo ? <img src={URL.createObjectURL(photo)} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""/> : <span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>Upload Photo</span>}
              </div>
              <input type="file" accept="image/*" style={{display:'none'}} onChange={e => setPhoto(e.target.files[0])}/>
            </label>
          </div>
          <div className="form-grid">
            <FormField label="Full Name" required><input className="form-control" value={form.full_name} onChange={e => setF('full_name', e.target.value)} required/></FormField>
            <FormField label="Email" required><input className="form-control" type="email" value={form.email} onChange={e => setF('email', e.target.value)} required/></FormField>
            <FormField label="Phone"><input className="form-control" value={form.phone} onChange={e => setF('phone', e.target.value)}/></FormField>
            <FormField label="WhatsApp"><input className="form-control" value={form.whatsapp} onChange={e => setF('whatsapp', e.target.value)}/></FormField>
            <FormField label="Password"><input className="form-control" type="password" value={form.password} onChange={e => setF('password', e.target.value)} placeholder="Default: Teacher@123"/></FormField>
            <FormField label="Designation"><input className="form-control" value={form.designation} onChange={e => setF('designation', e.target.value)}/></FormField>
            <FormField label="Qualification"><input className="form-control" value={form.qualification} onChange={e => setF('qualification', e.target.value)}/></FormField>
            <FormField label="Specialization"><input className="form-control" value={form.specialization} onChange={e => setF('specialization', e.target.value)}/></FormField>
            <FormField label="Joining Date"><input className="form-control" type="date" value={form.joining_date} onChange={e => setF('joining_date', e.target.value)}/></FormField>
            <FormField label="Salary (₹)"><input className="form-control" type="number" value={form.salary} onChange={e => setF('salary', e.target.value)}/></FormField>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add Teacher'}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!showView} onClose={() => setShowView(null)} title="Teacher Profile">
        {showView && <div>
          <div style={{display:'flex',gap:16,alignItems:'center',marginBottom:20,padding:16,background:'var(--surface-alt)',borderRadius:'var(--r-md)'}}>
            <Avatar src={showView.photo_url} name={showView.full_name} size="avatar-xl"/>
            <div><h2 style={{fontSize:'1.1rem',fontWeight:700}}>{showView.full_name}</h2><p style={{color:'var(--text-secondary)',fontSize:'0.85rem'}}>{showView.designation}</p><Badge type="info">{showView.employee_id}</Badge></div>
          </div>
          <InfoRow label="Email" value={showView.email}/>
          <InfoRow label="Phone" value={showView.phone}/>
          <InfoRow label="WhatsApp" value={showView.whatsapp}/>
          <InfoRow label="Qualification" value={showView.qualification}/>
          <InfoRow label="Specialization" value={showView.specialization}/>
          <InfoRow label="Joining Date" value={showView.joining_date}/>
          <InfoRow label="Salary" value={showView.salary ? `₹${parseFloat(showView.salary).toLocaleString()}` : '—'}/>
        </div>}
      </Modal>
    </div>
  );
}

// ====== EXAMS PAGE ======
export function Exams() {
  const { data: exams, loading, refetch } = useFetch('/exams');
  const { classes } = useClasses();
  const [form, setForm] = useState({ name:'', type:'unit_test', class_id:'', subject_id:'', exam_date:'', max_marks:100, passing_marks:33 });
  const [showAdd, setShowAdd] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [results, setResults] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const { loading: saving, submit } = useSubmit();

  const setF = (k, v) => setForm(f => ({...f, [k]: v}));
  const classOpts = classes.map(c => ({ value: c.id, label: `${c.name} - ${c.section}` }));
  const TYPE_OPTS = ['unit_test','mid_term','final','practical','assignment'].map(v => ({ value:v, label:v.replace('_',' ').replace(/\b\w/g, l => l.toUpperCase()) }));

  const loadSubjects = async (classId) => {
    setF('class_id', classId);
    if (!classId) return;
    const { data } = await api.get(`/classes/${classId}/subjects`);
    setSubjects(data);
  };

  const loadExamResults = async (exam) => {
    setShowResults(exam);
    const [resData, studs] = await Promise.all([
      api.get(`/exams/${exam.id}/results`),
      api.get(`/students?class_id=${exam.class_id}&limit=100`)
    ]);
    setResults(resData.data);
    setStudents(studs.data.data || []);
    const m = {};
    resData.data.forEach(r => { m[r.student_id] = r.marks_obtained; });
    setMarks(m);
  };

  const saveResults = async () => {
    const resultArr = students.map(s => ({ student_id: s.student_id, marks: marks[s.student_id] || 0 }));
    const r = await submit(() => api.post(`/exams/${showResults.id}/results`, { results: resultArr }), 'Results saved!');
  };

  const downloadReportCard = (studentId) => {
    window.open(`${api.defaults.baseURL}/reports/report-card/${studentId}/${showResults?.type}`, '_blank');
  };

  const columns = [
    { label: 'Exam', render: r => <div><div style={{fontWeight:700}}>{r.name}</div><div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{r.type?.replace('_',' ')}</div></div> },
    { label: 'Class', key: 'class_name' },
    { label: 'Subject', key: 'subject_name' },
    { label: 'Date', key: 'exam_date' },
    { label: 'Max Marks', key: 'max_marks' },
    { label: '', render: r => <button className="btn btn-primary btn-sm" onClick={() => loadExamResults(r)}>Enter Results</button> },
  ];

  return (
    <div>
      <PageHeader title="Exams & Results" subtitle="Manage examinations and student results"
        actions={<button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={15}/> Create Exam</button>} />
      <div className="card"><Table columns={columns} data={exams} loading={loading} /></div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Create Exam" size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/exams', form), 'Exam created!');
            if (r.success) { setShowAdd(false); refetch(); }
          }}>{saving ? 'Creating...' : 'Create Exam'}</button></>}>
        <div className="form-grid">
          <FormField label="Exam Name" required><input className="form-control" value={form.name} onChange={e => setF('name', e.target.value)}/></FormField>
          <FormField label="Type"><Select value={form.type} onChange={v => setF('type', v)} options={TYPE_OPTS}/></FormField>
          <FormField label="Class" required><Select value={form.class_id} onChange={loadSubjects} options={classOpts} placeholder="Select Class"/></FormField>
          <FormField label="Subject"><Select value={form.subject_id} onChange={v => setF('subject_id', v)} options={subjects.map(s => ({value:s.id, label:s.name}))} placeholder="Select Subject"/></FormField>
          <FormField label="Exam Date"><input className="form-control" type="date" value={form.exam_date} onChange={e => setF('exam_date', e.target.value)}/></FormField>
          <FormField label="Max Marks"><input className="form-control" type="number" value={form.max_marks} onChange={e => setF('max_marks', e.target.value)}/></FormField>
        </div>
      </Modal>

      <Modal open={!!showResults} onClose={() => setShowResults(null)} title={`Results: ${showResults?.name}`} size="lg"
        footer={<><button className="btn btn-secondary" onClick={() => setShowResults(null)}>Close</button><button className="btn btn-primary" onClick={saveResults} disabled={saving}>{saving ? 'Saving...' : 'Save Results'}</button></>}>
        {showResults && <div className="table-wrap"><table className="table"><thead><tr><th>Roll</th><th>Student</th><th>Marks /{showResults.max_marks}</th><th>Grade</th><th>Report</th></tr></thead>
          <tbody>{students.map(s => {
            const m = parseFloat(marks[s.student_id] || 0);
            const pct = m / showResults.max_marks;
            const grade = pct >= 0.9 ? 'A+' : pct >= 0.8 ? 'A' : pct >= 0.7 ? 'B' : pct >= 0.6 ? 'C' : pct >= 0.33 ? 'D' : 'F';
            return <tr key={s.student_id}><td style={{fontWeight:700}}>{s.roll_no}</td><td>{s.full_name}</td>
              <td><input type="number" min="0" max={showResults.max_marks} value={marks[s.student_id] || ''} onChange={e => setMarks(m => ({...m, [s.student_id]: e.target.value}))} style={{width:80,padding:'4px 8px',border:'1.5px solid var(--border-strong)',borderRadius:6,fontFamily:'Poppins',fontSize:'0.875rem'}}/></td>
              <td><span style={{fontWeight:700,color:grade==='F'?'var(--danger)':grade==='A+'?'var(--secondary)':'var(--text)'}}>{grade}</span></td>
              <td><button className="btn btn-secondary btn-sm" onClick={() => downloadReportCard(s.student_id)}><Download size={13}/></button></td>
            </tr>;
          })}</tbody></table></div>}
      </Modal>
    </div>
  );
}

// ====== LIBRARY PAGE ======
export function Library() {
  const { data: books, loading, refetch } = useFetch('/library/books');
  const { data: issues, loading: issuesLoading } = useFetch('/library/issues');
  const [tab, setTab] = useState('books');
  const [form, setForm] = useState({ title:'', author:'', isbn:'', category:'', total_copies:1 });
  const [showAdd, setShowAdd] = useState(false);
  const { loading: saving, submit } = useSubmit();
  const setF = (k, v) => setForm(f => ({...f, [k]: v}));

  const returnBook = async (id) => {
    const r = await submit(() => api.post(`/library/return/${id}`), 'Book returned!');
    if (r.success) toast.success(`Fine: ₹${r.data?.data?.fine || 0}`);
  };

  const bookCols = [
    { label: 'Title', render: r => <div><div style={{fontWeight:700}}>{r.title}</div><div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{r.author}</div></div> },
    { label: 'ISBN', key: 'isbn' },
    { label: 'Category', key: 'category' },
    { label: 'Total', key: 'total_copies' },
    { label: 'Available', render: r => <span style={{fontWeight:700, color: r.available_copies > 0 ? 'var(--secondary)' : 'var(--danger)'}}>{r.available_copies}</span> },
  ];

  const issueCols = [
    { label: 'Student', render: r => <div><div style={{fontWeight:700}}>{r.full_name}</div><div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{r.admission_no}</div></div> },
    { label: 'Book', render: r => <div><div style={{fontWeight:700}}>{r.title}</div><div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{r.author}</div></div> },
    { label: 'Issue Date', key: 'issue_date' },
    { label: 'Due Date', render: r => <span style={{fontWeight:700,color:new Date(r.due_date)<new Date()?'var(--danger)':'var(--text)'}}>{r.due_date}</span> },
    { label: '', render: r => <button className="btn btn-success btn-sm" onClick={() => returnBook(r.id)}>Return</button> },
  ];

  return (
    <div>
      <PageHeader title="Library" subtitle="Book management and issue tracking"
        actions={<button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={15}/> Add Book</button>} />
      <div className="tabs">
        <button className={`tab-btn ${tab==='books'?'active':''}`} onClick={() => setTab('books')}>All Books</button>
        <button className={`tab-btn ${tab==='issues'?'active':''}`} onClick={() => setTab('issues')}>Active Issues</button>
      </div>
      <div className="card">
        {tab==='books' ? <Table columns={bookCols} data={books} loading={loading}/> : <Table columns={issueCols} data={issues} loading={issuesLoading} emptyMsg="No books currently issued"/>}
      </div>
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Book" size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/library/books', form), 'Book added!');
            if (r.success) { setShowAdd(false); refetch(); }
          }}>{saving ? 'Adding...' : 'Add Book'}</button></>}>
        <div className="form-grid">
          <FormField label="Title" required><input className="form-control" value={form.title} onChange={e => setF('title',e.target.value)}/></FormField>
          <FormField label="Author"><input className="form-control" value={form.author} onChange={e => setF('author',e.target.value)}/></FormField>
          <FormField label="ISBN"><input className="form-control" value={form.isbn} onChange={e => setF('isbn',e.target.value)}/></FormField>
          <FormField label="Category"><input className="form-control" value={form.category} onChange={e => setF('category',e.target.value)}/></FormField>
          <FormField label="Total Copies"><input className="form-control" type="number" value={form.total_copies} onChange={e => setF('total_copies',e.target.value)}/></FormField>
        </div>
      </Modal>
    </div>
  );
}

// ====== REPORTS PAGE ======
export function Reports() {
  const { classes } = useClasses();
  const [classId, setClassId] = useState('');
  const [month, setMonth] = useState('');
  const classOpts = classes.map(c => ({ value: c.id, label: `${c.name} - ${c.section}` }));

  return (
    <div>
      <PageHeader title="Reports & Analytics" subtitle="Download reports and analyze school data" />
      <div className="grid-2" style={{ marginBottom: 24 }}>
        <div className="card">
          <h3 style={{fontWeight:700, marginBottom:16}}>📊 Attendance Reports</h3>
          <FormField label="Class"><Select value={classId} onChange={setClassId} options={classOpts} placeholder="Select Class"/></FormField>
          <FormField label="Month"><input className="form-control" type="month" value={month} onChange={e => setMonth(e.target.value)}/></FormField>
          <div style={{display:'flex',gap:10,marginTop:16}}>
            <button className="btn btn-primary" onClick={() => window.open(`${api.defaults.baseURL}/reports/attendance-csv?class_id=${classId}&month=${month}`)}>
              <Download size={15}/> Download CSV
            </button>
          </div>
        </div>
        <div className="card">
          <h3 style={{fontWeight:700, marginBottom:16}}>💰 Fee Reports</h3>
          <div style={{display:'flex',gap:10,marginTop:8,flexWrap:'wrap'}}>
            {['paid','pending','overdue'].map(s => (
              <button key={s} className="btn btn-secondary" onClick={() => window.open(`${api.defaults.baseURL}/reports/fees-csv?status=${s}`)}>
                <Download size={15}/> {s.charAt(0).toUpperCase()+s.slice(1)} Fees
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="card">
        <h3 style={{fontWeight:700, marginBottom:12}}>🎓 Student Report Cards</h3>
        <p style={{color:'var(--text-secondary)', fontSize:'0.875rem', marginBottom:16}}>Go to Exams page → Enter Results → Click download button for individual report cards (PDF)</p>
        <div style={{padding:'16px',background:'var(--surface-alt)',borderRadius:'var(--r-md)',fontSize:'0.85rem',color:'var(--text-secondary)'}}>
          Report cards include: subject-wise marks, grades, attendance percentage, and overall performance.
        </div>
      </div>
    </div>
  );
}

// ====== SETTINGS PAGE ======
export function Settings() {
  const [webhook, setWebhook] = useState('');
  const [saved, setSaved] = useState(false);
  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure school ERP system"/>
      <div className="grid-2">
        <div className="card">
          <h3 style={{fontWeight:700,marginBottom:16}}>📱 WhatsApp Webhook</h3>
          <FormField label="Webhook URL">
            <input className="form-control" value={webhook} onChange={e => setWebhook(e.target.value)} placeholder="https://your-whatsapp-api.com/send"/>
          </FormField>
          <p style={{fontSize:'0.78rem',color:'var(--text-muted)',marginTop:8}}>Set WHATSAPP_WEBHOOK_URL in backend .env file</p>
        </div>
        <div className="card">
          <h3 style={{fontWeight:700,marginBottom:16}}>🏫 School Information</h3>
          <FormField label="School Name"><input className="form-control" defaultValue="Vidya Mandir Sr. Secondary School"/></FormField>
          <FormField label="CBSE School Code"><input className="form-control" defaultValue="12345"/></FormField>
          <FormField label="Academic Year"><input className="form-control" defaultValue="2024-25"/></FormField>
        </div>
      </div>
    </div>
  );
}
